import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { EASE, Magnetic } from "@/components/motion/primitives";
import { useAuth } from "@/context/AuthContext";

/* seamless looping garden video — morning clip in light mode, evening clip in dark mode */
const CLIPS = {
  light: { video: "/hero-morning.mp4", poster: "/hero-morning.jpg" },
  dark: { video: "/hero-evening.mp4", poster: "/hero-evening.jpg" },
};
const FADE = 0.85; // seconds of crossfade at the loop seam
const HERO_DEFAULT = {
  eyebrow: "Curiosity breeds intimacy",
  headline: "Scripture asks questions.",
  accent: "So should you.",
  subhead: "From Eden\u2019s \u201cWhere art thou?\u201d to \u201cWhom say ye that I am?\u201d, Scripture is full of questions. Each one is an invitation. Explore them, and let them lead you closer to God.",
};

/* Two stacked copies of the clip crossfade near the end so the loop has no
   visible jump-cut. */
function SmoothVideo({ clip }) {
  const a = useRef(null);
  const b = useRef(null);
  const [front, setFront] = useState("a");
  const busy = useRef(false);

  useEffect(() => {
    const va = a.current;
    const vb = b.current;
    if (!va || !vb) return undefined;
    va.play().catch(() => {});
    const onTime = (e) => {
      const v = e.target;
      const isFront = (v === va && front === "a") || (v === vb && front === "b");
      if (!isFront || busy.current || !v.duration) return;
      if (v.currentTime >= v.duration - FADE) {
        busy.current = true;
        const other = v === va ? vb : va;
        other.currentTime = 0;
        other.play().catch(() => {});
        setFront(v === va ? "b" : "a");
        setTimeout(() => { busy.current = false; }, FADE * 1000);
      }
    };
    va.addEventListener("timeupdate", onTime);
    vb.addEventListener("timeupdate", onTime);
    return () => {
      va.removeEventListener("timeupdate", onTime);
      vb.removeEventListener("timeupdate", onTime);
    };
  }, [front]);

  const cls = (isFront) =>
    `absolute inset-0 h-full w-full object-cover transition-opacity ease-linear ${isFront ? "opacity-100" : "opacity-0"}`;

  return (
    <>
      <video key={`a-${clip.video}`} ref={a} data-testid="hero-video" data-clip={clip.video} className={cls(front === "a")} style={{ transitionDuration: `${FADE}s` }} autoPlay muted loop={false} playsInline preload="auto" poster={clip.poster}>
        <source src={clip.video} type="video/mp4" />
      </video>
      <video key={`b-${clip.video}`} ref={b} aria-hidden className={cls(front === "b")} style={{ transitionDuration: `${FADE}s` }} muted playsInline preload="auto" poster={clip.poster}>
        <source src={clip.video} type="video/mp4" />
      </video>
    </>
  );
}

export default function GardenHero() {
  const { theme } = useTheme();
  const clip = CLIPS[theme === "dark" ? "dark" : "light"];
  const reduced = useReducedMotion();
  const { openAuth, user } = useAuth();

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 5 * 60 * 1000,
  });
  const hero = settings?.hero || HERO_DEFAULT;

  /* one signature question, refreshed each day */
  const { data: daily } = useQuery({
    queryKey: ["hero-daily"],
    queryFn: async () => (await api.get("/questions/daily")).data,
  });

  /* mouse parallax on the backdrop */
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-1, 1], [12, -12]), { stiffness: 50, damping: 20 });
  const py = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 50, damping: 20 });
  const onMove = (e) => {
    if (reduced) return;
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  return (
    <section
      data-testid="hero"
      onPointerMove={onMove}
      className="relative -mt-16 flex min-h-[calc(88svh+4rem)] flex-col justify-center overflow-hidden bg-[#07090A] lg:min-h-[calc(min(82svh,56.25vw)+4rem)]"
    >
      {/* cinematic looping garden video (seamless) with subtle parallax */}
      <motion.div style={{ x: reduced ? 0 : px, y: reduced ? 0 : py }} className="absolute inset-[-1.5%]">
        {reduced ? (
          <img src={clip.poster} alt="" aria-hidden className="h-full w-full object-cover" />
        ) : (
          <SmoothVideo key={clip.video} clip={clip} />
        )}
      </motion.div>

      {/* cinematic scrims */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-transparent" />
      {/* mobile legibility wash — keeps hero copy readable over the bright rays */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-black/45 sm:bg-black/10" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(6,7,9,0.94)_2%,rgba(6,7,9,0.6)_30%,rgba(6,7,9,0.12)_58%,transparent_80%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_20%_100%,rgba(6,7,9,0.7),transparent_60%)]" />
      <div aria-hidden className="grain pointer-events-none absolute inset-0 opacity-30" />

      {/* drifting light motes */}
      {!reduced &&
        [
          { l: "14%", t: "32%", d: 0, s: 7 },
          { l: "72%", t: "22%", d: 1.5, s: 9 },
          { l: "84%", t: "58%", d: 0.8, s: 6 },
          { l: "40%", t: "18%", d: 2.2, s: 8 },
          { l: "60%", t: "70%", d: 1.1, s: 10 },
        ].map((m) => (
          <motion.span
            key={`${m.l}-${m.t}`}
            aria-hidden
            className="pointer-events-none absolute rounded-full bg-[#E8C36A] blur-[1px]"
            style={{ left: m.l, top: m.t, width: 4, height: 4 }}
            animate={{ y: [0, -18, 0], opacity: [0.15, 0.7, 0.15] }}
            transition={{ duration: m.s, delay: m.d, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}

      {/* content — headline left, today's question right on large screens; stacked below */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 pb-14 pt-24 sm:px-6 lg:flex-row lg:items-center lg:gap-12 lg:px-8">
        <div className="min-w-0 lg:flex-1">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}>
            <span data-testid="hero-eyebrow" className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.38em] text-[#E8C36A]">
              <span className="h-px w-10 bg-[#E8C36A]/70" /> {hero.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40, scale: 1.03 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.05, ease: EASE, delay: 0.35 }}
            data-testid="hero-headline"
            className="mt-6 origin-left font-serif text-5xl font-semibold leading-[0.92] tracking-tight text-[#FFF4E0] drop-shadow-[0_2px_30px_rgba(0,0,0,0.65)] sm:text-6xl lg:text-7xl"
          >
            {hero.headline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
            data-testid="hero-headline-accent"
            className="mt-2 font-serif text-4xl italic leading-[1] text-[#E8C36A] drop-shadow-[0_2px_24px_rgba(0,0,0,0.55)] sm:text-5xl lg:text-6xl"
          >
            {hero.accent}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.72 }}
            className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white/90 [text-shadow:0_1px_16px_rgba(0,0,0,0.85)] sm:text-lg sm:font-normal sm:text-white/80"
          >
            {hero.subhead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: EASE, delay: 0.88 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Magnetic>
              <Link to="/explore">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="hero-explore-button"
                  className="group inline-flex items-center gap-1 rounded-full bg-[#E8C36A] px-7 py-3.5 text-sm font-semibold text-[#07090A] shadow-[0_10px_40px_-10px_rgba(232,195,106,0.7)] transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(232,195,106,0.75)]"
                >
                  Explore the Questions
                  <ArrowRight className="h-4 w-4 -translate-x-1.5 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                </motion.button>
              </Link>
            </Magnetic>
            {!user && (
              <button
                onClick={openAuth}
                data-testid="hero-signup-button"
                className="text-sm font-medium text-white/80 underline-offset-4 transition-colors hover:text-white hover:underline [text-shadow:0_1px_12px_rgba(0,0,0,0.8)]"
              >
                Start your study
              </button>
            )}
          </motion.div>
        </div>

        {/* today's question box — right column on lg, full-width below on smaller screens */}
        {daily && (
          <div className="w-full min-w-0 animate-[qg-rise_0.7s_cubic-bezier(0.22,1,0.36,1)_both] lg:w-[380px] lg:shrink-0 xl:w-[420px]">
            <Link
              to={`/question/${daily.id}`}
              data-testid="hero-daily-featured"
              className="group block rounded-2xl border border-white/20 bg-black/40 p-6 shadow-2xl backdrop-blur-xl transition-colors hover:border-[#E8C36A]/70 hover:bg-black/50 sm:p-7"
            >
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#E8C36A]">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E8C36A]" /> Today&apos;s question
              </span>
              <p className="mt-4 break-words font-serif text-2xl italic leading-snug text-white sm:text-3xl">
                &ldquo;{daily.question_kjv}&rdquo;
              </p>
              <p className="mt-5 flex flex-wrap items-center gap-2 font-sans text-[11px] uppercase tracking-[0.15em] text-white/60">
                {daily.reference} · {daily.who_asked?.join(", ")}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 group-hover:text-[#E8C36A]" />
              </p>
            </Link>
          </div>
        )}
      </div>

      {/* scroll cue */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 sm:flex"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        Scroll
        <ChevronDown className="h-4 w-4" />
      </motion.div>
    </section>
  );
}
